import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from "react-router-dom";
import { useValidation } from "../../hooks/useValidation";
import { useAuthorization } from "../../hooks/useAuthorization";
import { useAccount, useIsAuthenticated, useMsal } from "@azure/msal-react";
import { Grid, MenuItem } from '@mui/material';
import DataTable from '../../components/DataTable';
import { tokenRequest } from "../../auth/authConfig";
import { SearchClients } from "../../services/client-file.service";
import TextField from "../../components/TextField";
import CustomDatePicker from '../../components/DatePicker';
import StyledButton from '../../components/elements/StyledButton';
import StyledDialog from '../../components/Dialog';
import SelectedDataRow from '../../components/elements/SelectedDataRow';
import Backdrop from '../../components/elements/Loader';
import SelectorTextField from "../../components/SelectorTextField";
import { FetchLookupData, CreateClientFile, UpdateClientFile, LinkClientFile, ReLinkClientFile, UnLinkClientFile, CheckDuplicateClients, OpenClient } from "../../services/client-file.service";
import { CheckCrmAvalability } from "../../services/crm-status-service"
import { format } from 'date-fns';
import { toast } from "react-toastify";
import Badge from '@mui/material/Badge';
import { useAppInsightsContext, useTrackEvent } from "@microsoft/applicationinsights-react-js";
import {SeverityLevel} from '@microsoft/applicationinsights-web';
import { useNavigationParameters } from "../../hooks/useNavigationParameters";
import * as Constants from "../../helper/constants";

function SearchClientFiles() {

    const [canRead] = useAuthorization();
    const isAuthenticated = useIsAuthenticated();
    const [appendExistingNavigationParameters,  removeDuplicatedNavigationParameters , prepareNavigationParameters] = useNavigationParameters();
    const { instance, accounts } = useMsal();
    const account = useAccount(accounts[0] || {});

    const [isValid] = useValidation();
    const navigate = useNavigate();

    const [gridColumns, setGridColumns] = useState([]);
    const [gridRows, setGridRows] = useState([]);

    const [searchParams, setSearchParams] = useSearchParams();
    const [firstName, setFirstName] = useState((searchParams && null != searchParams.get("firstname")) ? searchParams.get("firstname") : "");
    const [middleName, setMiddleName] = useState((searchParams && null != searchParams.get("middlename")) ? searchParams.get("middlename") : "");
    const [lastName, setLastName] = useState((searchParams && null != searchParams.get("lastname")) ? searchParams.get("lastname") : "");
    const [dateOfBirth, setDateOfBirth] = useState(searchParams ? searchParams.get("dateofbirth") : null);
    const [isEmptyResult, setIsEmptyResult] = useState(false);
    const [isSearched, setIsSearched] = useState(false);
    const [queryParams, setQueryParams] = useState("");
    const [selectedSearchResultRow, setSelectedSearchResultRow] = useState(null);
    const [isClientIdOnlyRecord, setIsClientIdOnlyRecord] = useState(false);
    const [isCrmIdOnlyRecord, setIsCrmIdOnlyRecord] = useState(false);
    const [isLinkedRecord, setIsLinkedRecord] = useState(false);
    const [selectedRecordDcpId, setSelectedRecordDcpId] = useState(0);
    const [selectedRecordCrmId, setSelectedRecordCrmId] = useState("");
    const [openPreviewGrid, setOpenPreviewGrid] = useState(false);
    const [contactMethods, setContactMethods] = useState([]);
    const [indigenousAncestries, setIndigenousAncestries] = useState([]);
    const clientBasicInfoComp = useRef();
    const clientContactInfoComp = useRef();
    const clientFinancialInfoComp = useRef();
    const [actionButtonTitle, setActionButtonTitle] = useState("Open Client File");
    const [actionButtonVisible, setActionButtonVisible] = useState(true);
    const [dateOfBirthError, setDateOfBirthError] = useState(false);
    const [dateOfBirthErrorMessage, setDateOfBirthErrorMessage] = useState("");
    const [firstNameError, setFirstNameError] = useState(false);
    const [middleNameError, setMiddleNameError] = useState(false);
    const [lastNameError, setLastNameError] = useState(false);
    const [isDataReady, setIsDataReady] = useState(true);
    const [trackEvents, setTrackEvents] = useState("");
    const appInsights = useAppInsightsContext();
    const trackEventsInsights = useTrackEvent(appInsights, "Search Clients : ", trackEvents);
    const [openRefineSearchDialog, setOpenRefineSearchDialog] = useState(false);
    const EMPTY_STRING = "";
    const INTERACTION_REQUIRED_AUTH_ERROR = "InteractionRequiredAuthError";
    const BROWSER_AUTH_ERROR = "BrowserAuthError";

    useEffect(() => {
        window.scrollTo(0, 0);
        LoadInitialData();
    }, []);

    useEffect(() => {
        LoadInitialData();
    }, [instance]);

    useEffect(() => {
        trackEventsInsights();
    }, [trackEvents]);

    useEffect(() => {
        if (!dateOfBirthError) setIsEmptyResult(false);
    }, [firstName, middleName, lastName, dateOfBirth]);

    const onSetQueryParams = () => {
        var qParams = "";
        if (firstName || middleName || lastName || dateOfBirth){           
            if (firstName) {
                qParams = `firstname=${firstName}`;
            }
            if (qParams && middleName) {
                qParams = qParams + `&middlename=${middleName}`;
            }
            if (!qParams && middleName) {
                qParams = `middlename=${middleName}`;
            }
            if (qParams && lastName) {
                qParams = qParams + `&lastname=${lastName}`;
            }
            if (!qParams && lastName) {
                qParams = `lastname=${lastName}`;
            }
            if (qParams && dateOfBirth) {
                qParams = qParams + `&dateofbirth=${dateOfBirth}`;
            }
            if (!qParams && dateOfBirth) {
                qParams =  `dateofbirth=${dateOfBirth}`;
            }
        }
        setQueryParams(qParams);
    };


    const setInitialGridColumns = () => {

        const columns = [];

        const columnHeaders = [{ headerName: 'Portal Id', width: 90, field: 'clientId', editable: false, hide: true },
        { headerName: 'CRM Id', width: 300, field: 'crmClientId', editable: false, hide: true },
        { headerName: 'CRM Id', width: 300, field: 'crmId', editable: false, hide: true },
        { headerName: 'Record Type', width: 180, field: 'recordType', renderReviewBanner: true, editable: false },
        { headerName: 'First Name', width: 170, field: 'firstName', editable: false },
        { headerName: 'Middle Name', width: 170, field: 'middleName', editable: false },
        { headerName: 'Last Name', width: 170, field: 'lastName', editable: false },
        { headerName: 'Date of Birth', type: 'date', width: 150, field: 'dateOfBirth', renderDate: true, editable: false },
        { headerName: 'Alias 1', width: 170, field: 'clientAlias1', editable: false },
        { headerName: 'Alias 2', width: 170, field: 'clientAlias2', editable: false },
        { headerName: 'Alias 3', width: 170, field: 'clientAlias3', editable: false },
        { headerName: 'Indigenous Ancestry', width: 170, field: 'indigenousAncestry', editable: false, hide: true },
        { headerName: 'Home Phone', width: 150, field: 'homePhoneNumber', editable: false, hide: true },
        { headerName: 'Mobile Phone', width: 150, field: 'mobilePhoneNumber', editable: false, hide: true },
        { headerName: 'Email', width: 250, field: 'email', editable: false },
        { headerName: 'Method of Contact Id', width: 170, field: 'preferredMethodOfContactId', editable: false, hide: true },
        { headerName: 'Indigenous Ancestry Id', width: 170, field: 'indigenousAncestryId', editable: false, hide: true },
        { headerName: 'Unique Id', width: 120, field: 'id', editable: false, hide: true }];

        onSetQueryParams();
        columnHeaders.forEach((columnHeader) => {
            columns.push(
                {
                    field: columnHeader.field,
                    renderHeader: () => (<div style={{ font: 'normal normal normal 15px/16px Franklin Gothic Medium', color: "#000000" }}>{columnHeader.headerName}</div>),
                    renderCell: (params) => (columnHeader.renderDate ? <>{(params.value != null && params.value != "") ? params.value : null}  </> : (columnHeader.renderReviewBanner ? <> {params.value} {((params.row.isCrmConflicted == true || params.row.isDcpConflicted == true) ? <Badge style={{ color: "#d32f2f", marginLeft: "5px" }}>**Needs Review** </Badge> : null)}</> : null)),
                    width: columnHeader.width,
                    editable: columnHeader.editable,
                    hide: columnHeader.hide
                },
            );
        });

        setGridColumns(columns);

    }


    function LoadInitialData() {
        if (firstName || middleName || lastName || dateOfBirth) {
            PerformSearchClickDetails();
        }
        else {
            setGridRows([]);
            setInitialGridColumns();
        }
        setIsDataReady(true);
    }

    function getSearchResultsFromCRMRowStyled(params) {
        if (params?.row.recordOrigin == 1) {
            return `data-grid-search-results-crm-row-style`
        }
        return null;
    };


    //#region OnChangeEvents
    const onFirstNameChanged = (event) => {
        if (event.target.value.length === 1) {
            setFirstNameError(true);
        } else {
            setFirstNameError(false);
            //setLastNameError(false);
        }
        setFirstName(event.target.value);

    };

    const onMiddleNameChanged = (event) => {
        if (event.target.value.length === 1) {
            setMiddleNameError(true);
        } else {
            setMiddleNameError(false);
        }
        setMiddleName(event.target.value);

    };

    const onLastNameChanged = (event) => {
        if (event.target.value.length === 1) {
            setLastNameError(true);
        } else {
            setLastNameError(false);
            //setFirstNameError(false);

        }
        setLastName(event.target.value);
    };

    const onBirthDateChanged = (date) => {
        setDateOfBirth(date);
        setDateOfBirthError(false);
    };
    const today = new Date();
    const maxBirthDate = new Date(today.setDate(today.getDate() - 1));

    const onDateOfBirthChange = (event) => {
        let eventValue = event.target.value;
        let showEventValue = eventValue.replace(/ /g, '').replace(/\-/g, '').replace(/\//g, '');
        let eventValueDate = (new Date(showEventValue)).toString();
        const checkDateValidity = (date) => {
            if ((new Date(date)).toString() === "Invalid Date") {
                setDateOfBirthError(true);
                setDateOfBirthErrorMessage("Invalid date format. Date format is dd-Mmm-yyyy.");
            } else if (new Date(date) > maxBirthDate) {
                setDateOfBirthError(true);
                setDateOfBirthErrorMessage("Invalid date format. Date format is dd-Mmm-yyyy.");
            } else {
                setDateOfBirthError(false);
                setDateOfBirth(date);
            }
        };
        if (showEventValue.length === 9) {
            checkDateValidity(eventValueDate);
        } else if (showEventValue.length === 8) {
            let inputArray = [...showEventValue];
            var formattedDate = "";
            formattedDate = inputArray.slice(4, 8).join('') + "-" + inputArray.slice(2, 4).join('') + "-" + inputArray.slice(0, 2).join('');
            let formattedNewDate = new Date(formattedDate);
            let isValidDate = new Date(formattedNewDate.setDate(formattedNewDate.getDate() + 1));
            let convertedUtcDate = new Date(isValidDate.setHours(0, 0, 0));
            checkDateValidity(convertedUtcDate);
        } else if (eventValue.length === 0) {
            setDateOfBirthError(false);
        } else {
            setDateOfBirthError(true);
            setDateOfBirthErrorMessage("Invalid date format. Date format is dd-Mmm-yyyy.");
        }
    };

    /**
     * Handles row click event in the search results grid and navigates directly to the client file (instead of client details as previous).
     * This function determines the type of client record (DCP-only, CRM-only, or linked) and
     * calls the OpenClient service to navigate to the appropriate client file dashboard.
     * 
     * @param {Object} params - The row click event parameters containing the selected row data
     * @param {Object} params.row - The selected row data containing client information
     * @param {string} params.row.clientId - The DCP client ID
     * @param {string} params.row.crmId - The CRM client ID
     * @param {number} params.row.clientFileId - The DCP client file ID
     * @param {string} params.row.clientFileCrmId - The CRM client file ID
     * @returns {Promise<void>} Promise that resolves when navigation is complete
     * @throws {Error} When token acquisition fails or OpenClient service call fails
     * @throws {InteractionRequiredAuthError} When user authentication session has expired
     * @throws {BrowserAuthError} When browser-related authentication errors occur
     */
    const handleOnRowClick = async (params) => {
        // Show loading indicator
        setIsDataReady(false);
        
        let dcpOnlyClientFileId = 0;
        let crmOnlyClientFileCrmId = "";
        let linkedDcpClientFileId = 0;

        setSelectedSearchResultRow(params.row);
        setIsClientIdOnlyRecord(false);
        setIsCrmIdOnlyRecord(false);

        if (params.row?.clientId !== EMPTY_STRING && params.row?.crmId === EMPTY_STRING) {
            dcpOnlyClientFileId = params.row?.clientFileId;
            setSelectedRecordDcpId(params.row?.clientId);
            setSelectedRecordCrmId("");
            setIsClientIdOnlyRecord(true);
        } else if (params.row?.clientId === EMPTY_STRING && params.row?.crmId !== EMPTY_STRING) {
            crmOnlyClientFileCrmId = params.row?.clientFileCrmId;
            setSelectedRecordDcpId(0);
            setSelectedRecordCrmId(params.row?.crmId);
            setIsCrmIdOnlyRecord(true);
        } else if (params.row?.clientId !== EMPTY_STRING && params.row?.crmId !== EMPTY_STRING) {
            linkedDcpClientFileId = params.row?.clientFileId;
            setSelectedRecordDcpId(params.row?.clientId);
            setSelectedRecordCrmId(params.row?.crmId);
            setIsLinkedRecord(true);
        }

        const openClientFromSearchResults = {
            "dcpOnlyClientFileId" : dcpOnlyClientFileId,
            "crmOnlyClientFileCrmId" : crmOnlyClientFileCrmId,
            "linkedDcpClientFileId" : linkedDcpClientFileId
        }

        await openClientFile(dcpOnlyClientFileId, crmOnlyClientFileCrmId, linkedDcpClientFileId, openClientFromSearchResults);
        
        // Hide loading indicator
        setIsDataReady(true);
        handleAutoScroll();
    };

    const handleAutoScroll = () => {
        const element = document.getElementById('scrollByClickedItem1');
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const onCreateClientFileClicked = () => {
        var params =  `${queryParams}&page=${Constants.SEARCHCLIENTS}`;
        navigate(`${prepareNavigationParameters(Constants.CLIENTFILEVIEWDETAILS, null, null, params)}`);
    }

    const onSearchClicked = async () => {
        setIsDataReady(false);
        setOpenPreviewGrid(false);
        await PerformSearchClickDetails();
    };

    const onEnterKeyPress = (e) => {
        if (e.key === "Enter" && (dateOfBirth || firstName || middleName || lastName) && !dateOfBirthError && !firstNameError && !middleNameError && !lastNameError) {
            e.preventDefault();
            onSearchClicked();
        }
    }

    const getSelectedDcpRecord = (recordDcpId) => {
        return recordDcpId == selectedRecordDcpId;
    }

    const getSelectedCrmRecord = (recordCrmId) => {
        return recordCrmId == selectedRecordCrmId;
    }

    const getContactMethod = (crmId) => {
        var result = 1;
        if ((null != contactMethods) && (!isNaN(crmId))) {
            var contactMethod = contactMethods.find(c => c.contactMethodCrmValue == crmId);
            if (null != contactMethod) {
                result = contactMethod.contactMethodId
            }
        }

        return result
    }

    const getIndigenousAncestry = (crmId) => {
        var result = 1;
        if ((null != indigenousAncestries) && (!isNaN(crmId))) {
            var indigenousAncestry = indigenousAncestries.find(c => c.indigenousAncestryCrmValue == crmId);
            if (null != indigenousAncestry) {
                result = indigenousAncestry.indigenousAncestryId
            }
        }
        return result
    }

    const compareSearchResult = (a, b) => {
        if (Date.parse(a.clientBasicInformation.createdOnInCRM) < Date.parse(b.clientBasicInformation.createdOnInCRM)) {
            return -1;
        }
        if (Date.parse(a.clientBasicInformation.createdOnInCRM) > Date.parse(b.clientBasicInformation.createdOnInCRM)) {
            return 1;
        }
        return 0;
    }

    const getMatchingCrmRecord = (selectedDcpId) => {
        var result = null;
        var dcpOnlyRecord = gridRows.find(r => r.clientFileId == selectedDcpId.replace("CF-", ""));
        if (null != dcpOnlyRecord) {
            var crmRecordsToMatch = gridRows.filter(r => (r.clientFileId == "" || r.clientFileId == null || r.clientFileId == 0))
            if (null != crmRecordsToMatch && crmRecordsToMatch.length > 0) {
                var matchingCrmRecords = crmRecordsToMatch.filter(c => c.clientBasicInformation.firstName == dcpOnlyRecord.clientBasicInformation.firstName && c.clientBasicInformation.middleName == dcpOnlyRecord.clientBasicInformation.middleName
                    && c.clientBasicInformation.lastName == dcpOnlyRecord.clientBasicInformation.lastName && new Date(c.clientBasicInformation.dateOfBirth).setHours(0, 0, 0, 0) == new Date(dcpOnlyRecord.clientBasicInformation.dateOfBirth).setHours(0, 0, 0, 0));
                if (null != matchingCrmRecords && matchingCrmRecords.length > 0) {
                    if (matchingCrmRecords.length == 1) {
                        result = matchingCrmRecords[0];
                        result.isCrmConflicted = false;
                    } else {
                        // Get Latest CRM Record
                        var sortedMatchingCrmRecords = matchingCrmRecords.sort(compareSearchResult)
                        result = sortedMatchingCrmRecords[(matchingCrmRecords.length - 1)];
                        result.isCrmConflicted = true;
                    }
                }
            }
        }
        return result;
    }

    const onSelectedRecordActionClick = async () => {
        let selectedRecord = null;
        let dcpOnlyClientFileId = 0;
        let crmOnlyClientFileCrmId = "";
        let linkedDcpClientFileId = 0;

        if (isClientIdOnlyRecord) {
            selectedRecord = gridRows.find(r => r.clientFileId == selectedRecordDcpId.replace("CF-", ""));
            dcpOnlyClientFileId = selectedRecord.clientFileId;

        } else if (isCrmIdOnlyRecord) {
            selectedRecord = gridRows.find(r => r.clientFileCrmId == selectedRecordCrmId);
            crmOnlyClientFileCrmId = selectedRecord.clientFileCrmId;

        } else if (isLinkedRecord) {
            selectedRecord = gridRows.find(r => r.clientFileCrmId == selectedRecordCrmId && r.clientFileId == selectedRecordDcpId.replace("CF-", ""));
            linkedDcpClientFileId = selectedRecord.clientFileId;
        }

        let openClientFromSearchResults = {
            "dcpOnlyClientFileId" : dcpOnlyClientFileId,
            "crmOnlyClientFileCrmId" : crmOnlyClientFileCrmId,
            "linkedDcpClientFileId" : linkedDcpClientFileId
        }

        await openClientFile(dcpOnlyClientFileId, crmOnlyClientFileCrmId, linkedDcpClientFileId, openClientFromSearchResults);
    }

    async function openClientFile (dcpOnlyClientFileId, crmOnlyClientFileCrmId, linkedDcpClientFileId, openClientFromSearchResults) {
        setTrackEvents(`Opening client from search results : ${JSON.stringify(openClientFromSearchResults)}`);
        appInsights.trackTrace({ message: `Opening client from search results : ${JSON.stringify(openClientFromSearchResults)}`, severityLevel: SeverityLevel.Information });

        if (account) {
            try {
                const response = await instance.acquireTokenSilent(tokenRequest);
                
                if (response) {
                    const data = await OpenClient(dcpOnlyClientFileId, crmOnlyClientFileCrmId, linkedDcpClientFileId, response.accessToken);
                    
                    if (data) {
                        if (data.checkCreateDuplicateClientFile) {
                            onCreateDuplicateClientConfirmClickOpen();
                        } else {
                            if (null != data.clientFile.clientFileId && "" != data.clientFile.clientFileId && data.clientFile.clientFileId > 0 && null != data.clientFile.clientFileCrmId && "" != data.clientFile.clientFileCrmId) {
                                let params = `${queryParams}&${Constants.CLIENTFILEIDQUERYSTRINGPARAM}=${data.clientFile.clientFileId}&crmId=${data.clientFile.clientFileCrmId}`;
                                navigate(`${prepareNavigationParameters(Constants.CLIENTFILEDASHBOARD, new Array(params))}&page=${Constants.SEARCHCLIENTS}`);
                               
                            }
                            else if ((null != data.clientFile.clientFileId && "" != data.clientFile.clientFileId && data.clientFile.clientFileId > 0) && (null == data.clientFile.clientFileCrmId || "" == data.clientFile.clientFileCrmId)) {
                                let params = `${queryParams}&${Constants.CLIENTFILEIDQUERYSTRINGPARAM}=${data.clientFile.clientFileId}`;
                                navigate(`${prepareNavigationParameters(Constants.CLIENTFILEDASHBOARD, new Array(params))}&page=${Constants.SEARCHCLIENTS}`);                                    
                            }
                            else if (null != data.clientFile.clientFileCrmId && "" != data.clientFile.clientFileCrmId) {
                                let params = `${queryParams}&crmId=${data.clientFile.clientFileCrmId}`;
                                navigate(`${prepareNavigationParameters(Constants.CLIENTFILEDASHBOARD, new Array(params))}&page=${Constants.SEARCHCLIENTS}`); 
                            }
                        }
                    } else {
                        toast.error("Cannot open this file as CRM is unavailable. Please try again later.", { position: "top-center", autoClose: 5000, theme: "colored" });
                        appInsights.trackException({ error: new Error('Cannot open this file as CRM is unavailable. Please try again later.'), severityLevel: SeverityLevel.Error });                           
                        navigate(`${prepareNavigationParameters(Constants.SEARCHCLIENTS, new Array(queryParams))}&page=${Constants.SEARCHCLIENTS}`);
                        window.location.reload();
                    }
                } else {
                    toast.error("Authentication failed. Please sign in again.", { position: "top-center", autoClose: 5000, theme: "colored" });
                    appInsights.trackException({ error: new Error('Authentication token acquisition failed - no response received'), severityLevel: SeverityLevel.Error });
                }
            } catch (error) {
                // Handle token acquisition errors specifically
                if (error.name === INTERACTION_REQUIRED_AUTH_ERROR) {
                    // Token has expired or user needs to re-authenticate
                    toast.error("Your session has expired. Please sign in again.", { position: "top-center", autoClose: 5000, theme: "colored" });
                    appInsights.trackException({ error: error, severityLevel: SeverityLevel.Warning });
                    // Optionally trigger re-authentication here
                    // instance.loginRedirect(loginRequest);
                } else if (error.name === BROWSER_AUTH_ERROR) {
                    // Browser-related authentication error
                    toast.error("Authentication error. Please refresh the page and try again.", { position: "top-center", autoClose: 5000, theme: "colored" });
                    appInsights.trackException({ error: error, severityLevel: SeverityLevel.Error });
                } else {
                    // General error handling for other cases
                    toast.error("An error occurred while opening the client file. Please try again.", { position: "top-center", autoClose: 5000, theme: "colored" });
                    appInsights.trackException({ error: error, severityLevel: SeverityLevel.Error });
                }
            }
        } else {
            toast.error("User account not found. Please sign in again.", { position: "top-center", autoClose: 5000, theme: "colored" });
            appInsights.trackException({ error: new Error('User account not found when attempting to open client file'), severityLevel: SeverityLevel.Error });
        }
    }

    async function PerformSearchClickDetails() {
        if (firstName || middleName || lastName || dateOfBirth) {
            var searchQueryParams = {
                "firstName" : firstName,
                "middleName" : middleName,
                "lastName" : lastName,
                "dateOfBirth" : (dateOfBirth ? new Date(dateOfBirth).toLocaleDateString() : "")
            }
            setTrackEvents("Searching by query params : " + JSON.stringify(searchQueryParams));
            appInsights.trackTrace({ message: "Searching by query params : " + JSON.stringify(searchQueryParams), severityLevel: SeverityLevel.Information });
            
            if (account) {
                instance.acquireTokenSilent(
                    tokenRequest
                ).then(async (response) => {
                    if (response) {
                        await FetchLookupData(response.accessToken).then((data) => {
                            if (data)
                                setContactMethods(data.contactMethods);
                            setIndigenousAncestries(data.indigenousAncestries);
                        });
                        //Search Value
                        await SearchClients(firstName, middleName, lastName, (dateOfBirth ? new Date(dateOfBirth).toLocaleDateString() : ""),  response.accessToken).then((data) => {                            
                            if (data && data != null && data?.clientFileSearchResults != null && data?.clientFileSearchResults.length > 0 && data?.clientFileSearchResults[0] != null) {
                                if (data.clientFileSearchResults[0].recordOrigin != null && data.clientFileSearchResults[0].recordOrigin == 0) {
                                    onSetQueryParams();
                                    setIsEmptyResult(true);
                                    setGridRows([]);
                                } else if (data.crmErrorStatus || data.dcpErrorStatus) {
                                    setOpenRefineSearchDialog(true);
                                    setIsEmptyResult(false);
                                    setGridRows(data.clientFileSearchResults);
                                } else {
                                    setIsEmptyResult(false);
                                    setGridRows(data.clientFileSearchResults);
                                }
                                setInitialGridColumns();
                            } else if (data && data != null && (data.crmErrorStatus || data.dcpErrorStatus)) {
                                setOpenRefineSearchDialog(true);
                                setGridRows(data.clientFileSearchResults);
                            }
                            setIsDataReady(true);
                          });
                    }
                });
            }
        }
        else {
            LoadInitialData();
        }
        setIsSearched(true);
    }

    const onCreateDuplicateClientConfirmClicked = () => {
        setOpenCreateDuplicateClientConfirmDialog(false);
        var crmRecord = gridRows.find(r => r.clientFileCrmId == selectedRecordCrmId);
        var updatedDateOfBirth = new Date(crmRecord.clientBasicInformation.dateOfBirth).setHours(0, 0, 0);
        var newRecord = {

            "ClientFileId": 0,
            "clientFileCrmId": crmRecord.clientFileCrmId,
            "isCrmConflicted": false,
            "isDcpConflicted": true,
            "crmClientId" : crmRecord.crmClientId,
            "clientBasicInformation": {
                "clientBasicInformationId": 0,
                "firstName": crmRecord.clientBasicInformation.firstName,
                "middleName": crmRecord.clientBasicInformation.middleName,
                "lastName": crmRecord.clientBasicInformation.lastName,
                "dateOfBirth": new Date(updatedDateOfBirth),
                "clientAlias1": crmRecord.clientBasicInformation.clientAlias1,
                "clientAlias2": crmRecord.clientBasicInformation.clientAlias2,
                "clientAlias3": crmRecord.clientBasicInformation.clientAlias3,
                "indigenousAncestryId": getIndigenousAncestry(Number(crmRecord.clientBasicInformation.indigenousAncestry?.indigenousAncestryCrmValue)),
                "createdOnInCRM": (null == crmRecord.clientBasicInformation.createdOnInCRM || "" == crmRecord.clientBasicInformation.createdOnInCRM) ?  null : new Date(crmRecord.clientBasicInformation.createdOnInCRM)
            },
            "clientContactDetails": {
                "clientContactInfoId": 0,
                "email": crmRecord.clientContactDetails.email,
                "phoneNumber": crmRecord.clientContactDetails.phoneNumber,
                "mobileNumber": crmRecord.clientContactDetails.mobileNumber,
                "PreferredMethodOfContactId": getContactMethod(Number(crmRecord.clientContactDetails.preferredMethodOfContact?.contactMethodCrmValue))
            },
            "ClientFinancialInformation": {
                "clientFinancialInformationId": 0,
                "financiallyEligible": Boolean(crmRecord.clientFinancialInformation.financiallyEligible),
                "familySize": crmRecord.clientFinancialInformation.familySize != '' ? crmRecord.clientFinancialInformation.familySize : null,
                "last12MonthsIncome": crmRecord.clientFinancialInformation.last12MonthsIncome != '' ? crmRecord.clientFinancialInformation.last12MonthsIncome : null,
                "last30DaysIncome": crmRecord.clientFinancialInformation.last30DaysIncome != '' ? crmRecord.clientFinancialInformation.last30DaysIncome : null,
                "totalEquityValue": crmRecord.clientFinancialInformation.totalEquityValue != '' ? crmRecord.clientFinancialInformation.totalEquityValue : null,
                "lastAssessmentDate": (null == crmRecord.clientFinancialInformation.lastAssessmentDate || "" == crmRecord.clientFinancialInformation.lastAssessmentDate) ? null : new Date(crmRecord.clientFinancialInformation.lastAssessmentDate)
            }
        };

        setTrackEvents("Creating Duplicate Client : " + JSON.stringify(newRecord));
        appInsights.trackTrace({ message: "Creating Duplicate Client : " + + JSON.stringify(newRecord), severityLevel: SeverityLevel.Information });
        
        if (account) {
            instance.acquireTokenSilent(
                tokenRequest
            ).then(async (response) => {
                if (response) {
                    CreateClientFile(newRecord, response.accessToken).then((data) => {
                        if (data && Number(data) && Number(data) > 0) {
                            // Navigate to Show the Newly Created Record from CRM Data (Already Linked).
                            var params=`${queryParams}&crmId=${selectedRecordCrmId}&clientFileId=${Number(data)}&page=searchresults`;
                            navigate(`${prepareNavigationParameters(Constants.CLIENTFILEDASHBOARD, new Array(params))}&page=${Constants.SEARCHCLIENTS}`);
                        }
                    });
                }
            });
        }
    }

    const [openCreateDuplicateClientConfirmDialog, setOpenCreateDuplicateClientConfirmDialog] = useState(false);

    const onCreateDuplicateClientConfirmClickOpen = () => {
        setOpenCreateDuplicateClientConfirmDialog(true);
    };

    const onCreateDuplicateClientConfirmClickClose = () => {
        setOpenCreateDuplicateClientConfirmDialog(false);
        // Create a DCP Conflicted Client File Aborted
        toast.success("Create Client File aborted.", { position: "top-center", autoClose: 5000, theme: "colored" });
    };

    const refineSearchDialogClose = () => {
        setOpenRefineSearchDialog(false);
    };
    
    if (isDataReady) {
        return (
            <Grid container direction="row" alignItems="flex-start" style={{
                padding: "0px 20px 0px 20px",
                background: "#F0F0F0 0% 0% no-repeat padding-box"
            }} >
                <Grid item xs={12} md={12} style={{
                    textAlign: "left", font: "normal normal bold 32px/39px Franklin Gothic Medium",
                    letterSpacing: "0px", color: " #000000", opacity: "1", margin: "20px 10px 20px 10px"
                }}>Search Client Files</Grid>
                <Grid item xs={12} sm={6} md={6} lg={3} style={{ margin: "10px 0px 10px 0px" }}>
                    <div style={{ maxWidth: "275px" }}>
                        <CustomDatePicker label="Date of Birth" placeholder="dd-Mmm-yyyy" maxDate={maxBirthDate}
                            error={dateOfBirthError ? true : null}
                            value={dateOfBirth}
                            handleOnChange={onBirthDateChanged}
                            onChange={onDateOfBirthChange}
                            onKeyDown={onEnterKeyPress}
                            helperText={dateOfBirthError ? dateOfBirthErrorMessage : null} />
                    </div>
                </Grid>
                <Grid item xs={12} sm={6} md={6} lg={3} style={{ margin: "10px 0px 10px 0px" }}>
                    <TextField label="First Name" placeholder="First Name"
                        error={firstNameError ? true : null}
                        value={firstName}
                        handleChange={onFirstNameChanged}
                        onKeyDown={onEnterKeyPress}
                        helperText={firstNameError ? "Minimum two characters are required" : null} />
                </Grid>
                <Grid item xs={12} sm={6} md={6} lg={3} style={{ margin: "10px 0px 10px 0px" }}>
                    <TextField label="Middle Name" placeholder="Middle Name"
                        error={middleNameError ? true : null}
                        value={middleName}
                        handleChange={onMiddleNameChanged}
                        onKeyDown={onEnterKeyPress}
                        helperText={middleNameError ? "Minimum two characters are required" : null} />
                </Grid>
                <Grid item xs={12} sm={6} md={6} lg={3} style={{ margin: "10px 0px 10px 0px" }}>
                    <TextField label="Last Name" placeholder="Last Name"
                        error={lastNameError ? true : null}
                        value={lastName}
                        handleChange={onLastNameChanged}
                        onKeyDown={onEnterKeyPress}
                        helperText={lastNameError ? "Minimum two characters are required" : null} />
                </Grid>
                <Grid item xs={12} md={6} style={{ margin: "0px 0px 10px 0px" }}>
                    <StyledButton variant={"outlined"} color="error" disabled={( !(dateOfBirth || firstName || middleName || lastName) || (dateOfBirthError || firstNameError || middleNameError || lastNameError)) ? true : false} onClick={onSearchClicked}>Search</StyledButton>
                </Grid>
                {(!(dateOfBirthError || firstNameError || middleNameError || lastNameError) && isSearched) &&
                    <Grid item xs={12} md={6} style={{ textAlign: "right", margin: "0px 0px 10px 0px", padding: "0px 40px 0px 0px" }}>
                        <StyledButton variant={"outlined"} color="error" onClick={onCreateClientFileClicked}>Create Client File</StyledButton>
                    </Grid>}

                <StyledDialog title="Create Duplicate Client File"
                    open={openCreateDuplicateClientConfirmDialog}
                    onClose={onCreateDuplicateClientConfirmClickClose}
                    message={"There is already a DC Portal Client File with the same First and Last names and date of birth. Are you sure you want to create a new file for this client?"}
                    onConfirmed={onCreateDuplicateClientConfirmClicked}
                    confirmationText="Create Client" isCancelRequired={true} />
                <StyledDialog title="Please Refine the Search Criteria"
                    open={openRefineSearchDialog}
                    onClose={refineSearchDialogClose}
                    message={"There are more search results than can be displayed. Try adding more search terms to narrow your search results."}
                    onConfirmed={refineSearchDialogClose}
                    confirmationText="OK" isCancelRequired={false} />
                <Grid item xs={12} md={12}>
                    <div id="scrollByClickedItem1" style={{ scrollMarginTop: "375px" }}>
                        {isSearched && (<>
                            {(isEmptyResult && !dateOfBirthError) && (<>
                                <Grid item xs={12} md={12} style={{ textAlign: "left", font: "normal normal bold 16px/18px Franklin Gothic Medium", letterSpacing: "0px", margin: "0px 0px 10px 0px" }}>
                                    No Client Files found for the search criteria.
                                </Grid>
                            </>)}
                            {openPreviewGrid ?
                                (isClientIdOnlyRecord ? <SelectedDataRow title={`Client File ${selectedSearchResultRow?.clientId} : ${selectedSearchResultRow?.firstName}  ${selectedSearchResultRow?.lastName} ${selectedSearchResultRow?.dateOfBirth} `} isFromSearch={true}
                                    actionButtonTitle={actionButtonTitle} actionButtonAction={onSelectedRecordActionClick} actionButtonVisible={actionButtonVisible}
                                    clientFileId={selectedSearchResultRow?.clientFileId} clientBasicInfoComp={clientBasicInfoComp} clientContactInfoComp={clientContactInfoComp} clientFinancialInfoComp={clientFinancialInfoComp} contactMethods={contactMethods} indigenousAncestries={indigenousAncestries} onClose={() => { setOpenPreviewGrid(false) }} /> :
                                    <SelectedDataRow title={`Client File ${selectedSearchResultRow?.clientId} : ${selectedSearchResultRow?.firstName}  ${selectedSearchResultRow?.lastName} ${selectedSearchResultRow?.dateOfBirth} `} clientFileId={selectedSearchResultRow?.clientFileId} isFromSearch={true}
                                        actionButtonTitle={actionButtonTitle} actionButtonAction={onSelectedRecordActionClick} actionButtonVisible={actionButtonVisible} contactMethods={contactMethods} indigenousAncestries={indigenousAncestries}
                                        clientFile={selectedSearchResultRow} onClose={() => { setOpenPreviewGrid(false) }} />)
                                :
                                (!isEmptyResult && gridRows && gridRows.length > 0) && <DataTable columns={gridColumns} rows={gridRows} disableSelection={true} onRowClick={handleOnRowClick} getRowStyled={getSearchResultsFromCRMRowStyled} />
                            }
                        </>)}
                    </div>
                </Grid>
                <Grid item xs={12} md={12} style={{ minHeight: "90vh" }} />
            </Grid>
        );
    } else {
        return (<Backdrop />);
    }

}

export default SearchClientFiles;